import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCartStore, useAuthStore } from '../store';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Trash2, Minus, Plus, ShoppingBag, Loader2 } from 'lucide-react';
import ProductImage from '../components/ProductImage';

const formatMoney = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount);

export default function CartPage() {
  const navigate = useNavigate();
  const { items, total, fetchCart, removeFromCart, updateQuantity, checkout, isLoading } = useCartStore();
  const { user, fetchUser } = useAuthStore();
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const handleCheckout = async () => {
    if (user.balance < total) {
      toast.error('Insufficient balance. Please add funds.');
      navigate('/deposits');
      return;
    }
    setCheckingOut(true);
    const result = await checkout();
    if (result.success) {
      toast.success('Order placed successfully!');
      await fetchUser();
      navigate(`/orders/${result.orderId}`);
    } else {
      toast.error(result.error);
    }
    setCheckingOut(false);
  };

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" /></div>;

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <ShoppingBag className="w-16 h-16 mx-auto text-zinc-600 mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Your cart is empty</h2>
        <p className="text-zinc-400 mb-6">Start shopping to add items to your cart.</p>
        <Button onClick={() => navigate('/')} className="bg-[#FFD700] text-black hover:bg-[#E5C100]">Browse Products</Button>
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <h1 className="text-2xl font-bold text-white font-['Outfit']">Shopping Cart ({items.length})</h1>
        {items.map((item) => (
          <Card key={item.product_id} className="bg-[#0A0A0A] border-white/10" data-testid={`cart-item-${item.product_id}`}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-20 h-20 bg-[#121212] rounded-lg overflow-hidden flex-shrink-0">
                <ProductImage src={item.thumbnail} alt={item.title} iconSize="w-8 h-8" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-white truncate">{item.title}</h3>
                <p className="text-[#FFD700] font-bold">{formatMoney(item.price)}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="h-8 w-8 border-white/10"><Minus className="w-3 h-3" /></Button>
                <span className="w-8 text-center text-white">{item.quantity}</span>
                <Button variant="outline" size="icon" onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="h-8 w-8 border-white/10" disabled={item.quantity >= item.stock}><Plus className="w-3 h-3" /></Button>
              </div>
              <div className="text-right">
                <p className="font-semibold text-white">{formatMoney(item.subtotal)}</p>
                <Button variant="ghost" size="sm" onClick={() => removeFromCart(item.product_id)} className="text-red-400 hover:text-red-300 p-0 h-auto"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="lg:col-span-1">
        <Card className="bg-[#0A0A0A] border-white/10 sticky top-24">
          <CardHeader><CardTitle className="text-white">Order Summary</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span>{formatMoney(total)}</span></div>
            <div className="border-t border-white/10 pt-4 flex justify-between text-lg font-bold"><span className="text-white">Total</span><span className="text-[#FFD700]">{formatMoney(total)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-zinc-400">Your Balance</span><span className={user?.balance >= total ? 'text-green-500' : 'text-red-500'}>{formatMoney(user?.balance || 0)}</span></div>
            <Button onClick={handleCheckout} disabled={checkingOut} className="w-full bg-[#FFD700] text-black hover:bg-[#E5C100] font-bold gold-glow" data-testid="checkout-btn">
              {checkingOut ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Processing...</> : 'Checkout'}
            </Button>
            {user?.balance < total && <p className="text-xs text-red-400 text-center">Insufficient balance. Add {formatMoney(total - (user?.balance || 0))} more.</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
